import { checkSlug, QUOTAS, slugify } from '@xb/shared';
import { type FormEvent, useId, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../api.js';
import { ErrorNotice } from '../components/Notice.js';
import { useAction, useCurrentUser } from '../hooks.js';

export function NewAppPage() {
  const navigate = useNavigate();
  const me = useCurrentUser();
  const ids = { name: useId(), slug: useId(), slugHint: useId(), type: useId() };
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [type, setType] = useState<'static' | 'server'>('static');

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const check = effectiveSlug ? checkSlug(effectiveSlug) : undefined;
  const slugInvalid = !!check && !check.ok;
  const atQuota = me.appCount >= QUOTAS.appsPerBuilder;

  const submit = useAction(async (e: FormEvent) => {
    e.preventDefault();
    if (!check?.ok || !name.trim()) return;
    const { app } = await api.createApp({ slug: check.slug, name: name.trim(), type });
    navigate(`/apps/${encodeURIComponent(app.slug)}`);
  });

  return (
    <div className="narrow">
      <div className="page-head">
        <h1>New app</h1>
      </div>
      {atQuota ? (
        <div className="notice notice-warn">
          You already have {QUOTAS.appsPerBuilder} apps, which is the limit for now. Delete one to make room.
        </div>
      ) : null}
      <form className="card" onSubmit={(e) => void submit.run(e)} noValidate>
        <div className="field">
          <label htmlFor={ids.name}>Name</label>
          <input
            id={ids.name}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            required
            autoComplete="off"
            placeholder="Monthly P&L dashboard"
          />
          <p className="hint">What you and your team will call it.</p>
        </div>

        <div className="field">
          <label htmlFor={ids.slug}>Web address</label>
          <input
            id={ids.slug}
            type="text"
            className="mono"
            value={effectiveSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value.toLowerCase());
            }}
            aria-describedby={ids.slugHint}
            aria-invalid={slugInvalid}
            autoComplete="off"
            spellCheck={false}
            placeholder="monthly-pl"
          />
          {slugInvalid ? (
            <p className="error-text" id={ids.slugHint}>
              Must be {check.reason}.
            </p>
          ) : (
            <p className="hint" id={ids.slugHint}>
              {effectiveSlug ? (
                <>
                  Your app will live at <span className="mono">{effectiveSlug}.apps.…</span>. This can't be
                  changed later.
                </>
              ) : (
                'Lower-case letters, digits and hyphens. Suggested from the name.'
              )}
            </p>
          )}
        </div>

        <fieldset>
          <legend id={ids.type}>What kind of app is it?</legend>
          <label className="radio">
            <input
              type="radio"
              name="type"
              value="static"
              checked={type === 'static'}
              onChange={() => setType('static')}
            />
            <span className="title">Static site</span>
            <span className="desc">
              HTML and JavaScript built with a tool like Vite. Runs entirely in the browser.
            </span>
          </label>
          <label className="radio">
            <input
              type="radio"
              name="type"
              value="server"
              checked={type === 'server'}
              onChange={() => setType('server')}
            />
            <span className="title">Server app</span>
            <span className="desc">
              Node, Python, or a Dockerfile. Runs on AWS Lambda with no local disk — it is given a database
              table and file storage instead. See docs/HOSTING.md for what your app needs to do.
            </span>
          </label>
        </fieldset>

        <ErrorNotice error={submit.error} />

        <div className="btn-row">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submit.pending || atQuota || !name.trim() || !check || !check.ok}
          >
            {submit.pending ? 'Creating…' : 'Create app'}
          </button>
          <Link to="/" className="btn">
            Cancel
          </Link>
        </div>
        <p className="hint" style={{ marginTop: '0.75rem' }}>
          Creating an app sets up its web address, which usually takes 5–10 minutes. You can upload your first
          version while that happens.
        </p>
      </form>
    </div>
  );
}
